import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboardAnalytics, type AnalyticsPeriod, type TicketCategory } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Layout } from "@/components/layout";
import {
  CheckCircle2,
  Clock,
  Inbox,
  Loader2,
  MessageSquare,
  Database,
  Target,
  Zap,
  Bot,
  HelpCircle,
  Users,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  KPICard,
  PeriodSelector,
  TicketsClosedChart,
  ResolutionTimeChart,
  OpenTicketBuckets,
  AgentPerformanceTable,
} from "@/components/dashboard";

// Categories for filtering
const CATEGORIES: { value: TicketCategory | "all"; label: string }[] = [
  { value: "all", label: "All Categories" },
  { value: "billing", label: "Billing" },
  { value: "feature_request", label: "Feature request" },
  { value: "general_support", label: "General support" },
  { value: "hardware_issue", label: "Hardware issue" },
  { value: "logistics", label: "Logistics" },
  { value: "onboarding", label: "Onboarding" },
  { value: "payment_issue", label: "Payment issue" },
  { value: "tech_bug", label: "Tech bug" },
  { value: "tech_question", label: "Tech question" },
  { value: "tech_request", label: "Tech request" },
  { value: "needs_categorisation", label: "Needs categorisation" },
];

function formatDuration(minutes: number): string {
  if (minutes === 0) return '-';
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

export function DashboardPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('7d');
  const [category, setCategory] = useState<TicketCategory | "all">("all");

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["dashboard-analytics", period, category],
    queryFn: () => getDashboardAnalytics(period, category === "all" ? undefined : category),
    refetchInterval: 300000, // Refresh every 5 minutes
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-destructive">Failed to load dashboard analytics</p>
          <p className="text-sm text-muted-foreground mt-2">
            Make sure the API is running and you're authenticated.
          </p>
        </div>
      </Layout>
    );
  }

  const { kpis, agentPerformance, ragMetrics } = data || {
    kpis: null,
    agentPerformance: [],
    ragMetrics: null,
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Ticket and AI performance metrics
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={category} onValueChange={(value) => setCategory(value as TicketCategory | "all")}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <PeriodSelector
              period={period}
              onPeriodChange={setPeriod}
              onRefresh={() => refetch()}
              isLoading={isFetching}
            />
          </div>
        </div>

        {/* KPI Cards Grid - Top Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title="Closed Tickets"
            value={kpis?.totalClosed ?? 0}
            subtitle="in selected period"
            icon={CheckCircle2}
          />
          <KPICard
            title="Open Tickets"
            value={kpis?.totalOpen ?? 0}
            subtitle="current"
            icon={Inbox}
          />
          <KPICard
            title="Avg Resolution Time"
            value={formatDuration(kpis?.avgResolutionTimeMinutes ?? 0)}
            subtitle="in selected period"
            icon={Clock}
          />
          <KPICard
            title="1-Response Resolved"
            value={kpis?.oneResponseResolutions ?? 0}
            subtitle="single message resolution"
            icon={Zap}
          />
        </div>

        {/* KPI Cards Grid - Second Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title="AI Draft Good"
            value={kpis?.closedWithoutAIChat ?? 0}
            subtitle="closed without follow-up"
            icon={Bot}
          />
          <KPICard
            title="AI Draft Refined"
            value={kpis?.closedWithAIChat ?? 0}
            subtitle="agent used AI chat"
            icon={MessageSquare}
          />
          <KPICard
            title="AI Said 'I Don't Know'"
            value={kpis?.aiSaidIDontKnow ?? 0}
            subtitle="no relevant results"
            icon={HelpCircle}
          />
          <KPICard
            title="Total Agents"
            value={agentPerformance?.length ?? 0}
            subtitle="active in period"
            icon={Users}
          />
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 md:grid-cols-2">
          <TicketsClosedChart data={kpis?.closedByDay ?? []} />
          <ResolutionTimeChart data={kpis?.resolutionTimeByDay ?? []} />
        </div>

        {/* Open Tickets by Age */}
        {kpis?.openTicketBuckets && (
          <OpenTicketBuckets buckets={kpis.openTicketBuckets} />
        )}

        {/* Agent Performance Table */}
        <AgentPerformanceTable agents={agentPerformance ?? []} />

        {/* RAG Metrics Section */}
        <Card>
          <CardHeader>
            <CardTitle>RAG System Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Conversations</span>
                </div>
                <div className="text-2xl font-bold">
                  {ragMetrics?.totalConversations ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">last 30 days</p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Vector Store</span>
                </div>
                <div className="text-2xl font-bold">
                  {ragMetrics?.totalVectors?.toLocaleString() ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">indexed vectors</p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Avg Confidence</span>
                </div>
                <div className="text-2xl font-bold">
                  {((ragMetrics?.avgConfidence ?? 0) * 100).toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">response quality</p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Last Sync</span>
                </div>
                <div className="text-2xl font-bold">
                  {ragMetrics?.syncStatus?.help?.lastSync
                    ? formatDistanceToNow(new Date(ragMetrics.syncStatus.help.lastSync), {
                        addSuffix: true,
                      })
                    : "Never"}
                </div>
                <p className="text-xs text-muted-foreground">help center</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sync Status */}
        <Card>
          <CardHeader>
            <CardTitle>Knowledge Sources Sync Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(ragMetrics?.syncStatus ?? {}).map(([source, status]) => (
                <div
                  key={source}
                  className="flex items-center justify-between border-b pb-3 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="capitalize">
                      {source}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {status.count.toLocaleString()} vectors
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {status.lastSync
                      ? formatDistanceToNow(new Date(status.lastSync), {
                          addSuffix: true,
                        })
                      : "Never synced"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
